import React, { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { MatrixHero } from "../../common/components/MatrixHero";
import { TypewriterText } from "../../common/components/TypewriterText";
import { useI18n } from "../../i18n/I18nProvider";
import "../../scss/reports.scss";

interface ReportDocument {
    id: string;
    title: string;
    link?: string;
    file?: string;
    added_date?: string;
    dect?: string;
    description?: string;
    size?: string;
}

interface ReportMonthGroup {
    id: string;
    label: string;
    reports: ReportDocument[];
}

const REPORTS_MANIFEST_PATH = "/reports/reports.json";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getPublicUrl = (path: string) => {
    if (/^(https?:)?\/\//.test(path) || path.startsWith("blob:") || path.startsWith("data:")) {
        return path;
    }

    const publicUrl = process.env.PUBLIC_URL || "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${publicUrl}${normalizedPath}`;
};

const ReportsPage: React.FC = () => {
    const { t, language } = useI18n();
    const [ready, setReady] = useState(false);
    const [reports, setReports] = useState<ReportDocument[]>([]);
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [viewerError, setViewerError] = useState(false);
    const [numPages, setNumPages] = useState(0);
    const [scale, setScale] = useState(1);
    const [viewerWidth, setViewerWidth] = useState(0);
    const viewerBodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        fetch(getPublicUrl(REPORTS_MANIFEST_PATH), { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Reports manifest is not available");
                }
                return response.json();
            })
            .then((data: unknown) => {
                if (!Array.isArray(data)) {
                    throw new Error("Reports manifest must be an array");
                }
                setReports(data as ReportDocument[]);
                setLoadError(false);
            })
            .catch((error) => {
                if ((error as Error).name !== "AbortError") {
                    setReports([]);
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!reports.length) {
            setSelectedReportId(null);
            return;
        }

        setSelectedReportId((currentId) => {
            if (currentId && reports.some((report) => report.id === currentId)) {
                return currentId;
            }
            return reports[0].id;
        });
    }, [reports]);

    const selectedReport = useMemo(
        () => reports.find((report) => report.id === selectedReportId) || null,
        [reports, selectedReportId]
    );

    const selectedReportUrl = selectedReport
        ? (() => {
              const base = getPublicUrl(selectedReport.link || selectedReport.file || "");
              const v = selectedReport.added_date ? selectedReport.added_date.replace(/-/g, "") : "";
              const sep = base.includes("?") ? "&" : "?";
              return v ? `${base}${sep}v=${v}` : base;
          })()
        : "";

    const groupedReports = useMemo<ReportMonthGroup[]>(() => {
        const locale = language === "pl" ? "pl-PL" : "en-US";
        const monthFormatter = new Intl.DateTimeFormat(locale, {
            month: "long",
            year: "numeric",
        });

        const sortedReports = [...reports].sort((a, b) => {
            const dateA = new Date(a.added_date || 0).getTime();
            const dateB = new Date(b.added_date || 0).getTime();
            return dateB - dateA;
        });

        return sortedReports.reduce<ReportMonthGroup[]>((groups, report) => {
            const date = report.added_date ? new Date(report.added_date) : null;
            const isValidDate = date !== null && !Number.isNaN(date.getTime());
            const groupId = isValidDate
                ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
                : "unknown";
            const groupLabel = isValidDate ? monthFormatter.format(date) : "Bez daty";
            const existingGroup = groups.find((group) => group.id === groupId);

            if (existingGroup) {
                existingGroup.reports.push(report);
            } else {
                groups.push({ id: groupId, label: groupLabel, reports: [report] });
            }

            return groups;
        }, []);
    }, [language, reports]);

    useEffect(() => {
        setViewerError(false);
        setNumPages(0);
        setScale(1);
    }, [selectedReportId]);

    useEffect(() => {
        const element = viewerBodyRef.current;
        if (!element) return;

        const updateWidth = () => setViewerWidth(element.clientWidth);

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, []);

    const horizontalPadding = viewerWidth <= 640 ? 20 : 40;
    const maxPageWidth = viewerWidth <= 640 ? viewerWidth - horizontalPadding : Math.min(viewerWidth - horizontalPadding, 920);
    const pageWidth = Math.max(260, (viewerWidth > 0 ? maxPageWidth : 720) * scale);

    const zoomOut = () => {
        setScale((currentScale) => Math.max(Number((currentScale - 0.1).toFixed(2)), 0.7));
    };

    const zoomIn = () => {
        setScale((currentScale) => Math.min(Number((currentScale + 0.1).toFixed(2)), 1.6));
    };

    const listHeight = Math.min(460, Math.max(190, reports.length * 118 + groupedReports.length * 42 + 86));

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("reports-page", { "reports-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("reports_section_label")}
        >
            <div className="reports-page__grid" aria-hidden="true" />
            <div className="reports-page__glow" aria-hidden="true" />

            <MatrixHero
                title={t("reports_hero_title")}
                subtitle={t("reports_hero_subtitle")}
                density={0.35}
            />

            <div className="reports-page__inner">
                <article className="reports-intro">
                    <span className="reports-intro__kicker">PDF</span>
                    <h2 className="reports-intro__title">{t("reports_intro_title")}</h2>
                    <p className="reports-intro__text">
                        <TypewriterText text={t("reports_intro_text")} active={ready} speed={2} />
                    </p>
                </article>

                <div className="reports-browser">
                    <aside
                        className="reports-list"
                        aria-labelledby="reports-list-title"
                        style={{ "--reports-list-mobile-height": `${listHeight}px` } as React.CSSProperties}
                    >
                        <div className="reports-list__header">
                            <h2 id="reports-list-title" className="reports-list__title">
                                {t("reports_list_title")}
                            </h2>
                            <span className="reports-list__count">{reports.length}</span>
                        </div>

                        {isLoading && (
                            <p className="reports-list__status">{t("reports_loading")}</p>
                        )}

                        {!isLoading && loadError && (
                            <p className="reports-list__status reports-list__status--error">
                                {t("reports_load_error")}
                            </p>
                        )}

                        {!isLoading && !reports.length && (
                            <div className="reports-empty">
                                <h3 className="reports-empty__title">{t("reports_empty_title")}</h3>
                                <p className="reports-empty__text">{t("reports_empty_text")}</p>
                            </div>
                        )}

                        {!!groupedReports.length && (
                            <div className="reports-list__groups">
                                {groupedReports.map((group, groupIndex) => (
                                    <section key={group.id} className="reports-month" aria-label={group.label}>
                                        <h3 className="reports-month__title">{group.label}</h3>
                                        <ul className="reports-list__items">
                                            {group.reports.map((report, reportIndex) => {
                                                const isActive = report.id === selectedReportId;
                                                const reportDesc = report.dect || report.description;

                                                return (
                                                    <li key={report.id} className="reports-list__item">
                                                        <button
                                                            type="button"
                                                            className={classNames("report-card", {
                                                                "report-card--active": isActive,
                                                            })}
                                                            onClick={() => setSelectedReportId(report.id)}
                                                            aria-current={isActive ? "true" : undefined}
                                                            style={{ animationDelay: `${(groupIndex + reportIndex) * 0.06}s` }}
                                                        >
                                                            <span className="report-card__icon" aria-hidden="true">
                                                                PDF
                                                            </span>
                                                            <span className="report-card__content">
                                                                <span className="report-card__title">{report.title}</span>
                                                                {reportDesc && (
                                                                    <span className="report-card__desc">
                                                                        {reportDesc}
                                                                    </span>
                                                                )}
                                                                <span className="report-card__meta">
                                                                    {report.added_date && <span>{report.added_date}</span>}
                                                                    {report.size && <span>{report.size}</span>}
                                                                </span>
                                                            </span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>
                                ))}
                            </div>
                        )}
                    </aside>

                    <section className="reports-viewer" aria-labelledby="reports-viewer-title">
                        <div className="reports-viewer__header">
                            <div>
                                <span className="reports-viewer__kicker">PDF VIEWER</span>
                                <h2 id="reports-viewer-title" className="reports-viewer__title">
                                    {selectedReport?.title || t("reports_viewer_title")}
                                </h2>
                            </div>

                            {selectedReport && (
                                <div className="reports-viewer__actions">
                                    <a
                                        href={selectedReportUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="reports-viewer__action"
                                    >
                                        {t("reports_open_new_tab")}
                                    </a>
                                    <span className="reports-viewer__pagesInfo">
                                        Strony: {numPages || "—"}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="reports-viewer__body" ref={viewerBodyRef}>
                            {!selectedReport && (
                                <div className="reports-viewer__placeholder">
                                    {t("reports_select_prompt")}
                                </div>
                            )}

                            {selectedReport && viewerError && (
                                <div className="reports-viewer__placeholder reports-viewer__placeholder--error">
                                    {t("reports_viewer_error")}
                                </div>
                            )}

                            {selectedReport && !viewerError && (
                                <div className="reports-pdf-shell">
                                    <div className="reports-pdf-toolbar" aria-label="Narzędzia podglądu PDF">
                                        <button
                                            type="button"
                                            className="reports-pdf-toolbar__button"
                                            onClick={zoomOut}
                                            disabled={scale <= 0.7}
                                        >
                                            −
                                        </button>
                                        <span className="reports-pdf-toolbar__counter">
                                            {Math.round(scale * 100)}%
                                        </span>
                                        <button
                                            type="button"
                                            className="reports-pdf-toolbar__button"
                                            onClick={zoomIn}
                                            disabled={scale >= 1.6}
                                        >
                                            +
                                        </button>
                                    </div>

                                    <Document
                                        key={selectedReport.id}
                                        file={selectedReportUrl}
                                        className="reports-viewer__pdf"
                                        loading={
                                            <div className="reports-viewer__placeholder">
                                                {t("reports_loading")}
                                            </div>
                                        }
                                        error={
                                            <div className="reports-viewer__placeholder reports-viewer__placeholder--error">
                                                {t("reports_viewer_error")}
                                            </div>
                                        }
                                        onLoadSuccess={({ numPages: loadedPages }) => {
                                            setNumPages(loadedPages);
                                        }}
                                        onLoadError={() => setViewerError(true)}
                                    >
                                        {Array.from(new Array(numPages), (_, index) => (
                                            <Page
                                                key={`report-page-${selectedReport.id}-${index + 1}`}
                                                pageNumber={index + 1}
                                                width={pageWidth}
                                                loading={
                                                    <div className="reports-viewer__page-loading">
                                                        Ładowanie strony {index + 1}…
                                                    </div>
                                                }
                                                renderAnnotationLayer
                                                renderTextLayer
                                            />
                                        ))}
                                    </Document>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </section>
    );
};

export default ReportsPage;